/**
 * MixVoIP iframe hand-over.
 *
 * The cockpit calls our /auth endpoint server-side and loads the URL it returns
 * in an iframe. This module runs on the other end of that URL: it redeems the
 * one-time token for an Elora session JWT.
 *
 * Deliberately does not go through apiService: apiRequest always resolves a B2C
 * token via MSAL and redirects to login when it cannot, which is precisely the
 * flow the hand-over exists to avoid. Redeem is authenticated by the token in
 * the body plus the APIM subscription key.
 */

import axios from 'axios';

import { API_SUBSCRIPTION_KEY, BASE_API_URLS } from '../config/appConfig';
import { PartnerSession } from './partnerSession';

interface RedeemResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  iframe_token_ttl_seconds?: number;
}

interface SessionJwtClaims {
  sub?: string;
  company_id?: string;
}

// The redeemed JWT's `sub`/`company_id` claims are what the backend actually
// authorizes every subsequent request against (see mint_elora_session_jwt on
// the backend) — reading identity from the token itself, rather than from
// URL query params the iframe host could set to anything, means the frontend
// never has a user/company id in hand that the backend didn't put there.
// Not signature-verified client-side: that's the backend's job on every call
// that uses this token, so a tampered payload just fails those calls.
const decodeSessionClaims = (jwt: string): SessionJwtClaims => {
  try {
    const payload = jwt.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as SessionJwtClaims;
  } catch {
    return {};
  }
};

/** Exchange the one-time iframe token for an Elora session. Single use. */
export const redeemIframeToken = async (
  token: string
): Promise<PartnerSession> => {
  const { data } = await axios.post<
    { message?: RedeemResponse } & RedeemResponse
  >(
    `${BASE_API_URLS.agent}/integrations/mixvoip/auth/redeem`,
    { token },
    {
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': API_SUBSCRIPTION_KEY.agent,
      },
    }
  );

  // The API wraps success payloads in `message`; tolerate both shapes so a
  // change there does not silently strand the iframe on a spinner.
  const payload = (data?.message ?? data) as RedeemResponse | undefined;
  if (!payload?.access_token) {
    throw new Error('Redeem did not return an access token');
  }

  const expiresInMs = (payload.expires_in ?? 3600) * 1000;
  const claims = decodeSessionClaims(payload.access_token);
  return {
    accessToken: payload.access_token,
    expiresAt: Date.now() + expiresInMs,
    userId: claims.sub,
    companyId: claims.company_id,
  };
};
